import { Settings } from '@/constants/settings';
import { Lang } from '@/store/player/types';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

function isEnglishLang(): boolean {
  return Settings.LANG_TYPE === Lang.EN;
}

function padTimePart(value: number): string {
  return String(value).padStart(2, '0');
}

function formatHours(date: Date): string {
  return `${padTimePart(date.getHours())}:${padTimePart(date.getMinutes())}`;
}

function formatDate(date: Date): string {
  return `${padTimePart(date.getDate())}.${padTimePart(date.getMonth() + 1)}.${date.getFullYear()}`;
}

function chooseRussianWord(value: number, one: string, few: string, many: string): string {
  const mod100 = value % 100;
  const mod10 = value % 10;

  if (mod100 >= 11 && mod100 <= 14) {
    return many;
  }
  if (mod10 === 1) {
    return one;
  }
  if (mod10 >= 2 && mod10 <= 4) {
    return few;
  }
  return many;
}

function formatMinutesAgo(minutes: number): string {
  if (isEnglishLang()) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  return `${minutes} ${chooseRussianWord(minutes, 'минуту', 'минуты', 'минут')} назад`;
}

function formatHoursAgo(hours: number): string {
  if (isEnglishLang()) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  return `${hours} ${chooseRussianWord(hours, 'час', 'часа', 'часов')} назад`;
}

function getStartOfDayTime(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function formatChatSentTime(messageTime?: number, fallbackSentAt: string = ''): string {
  if (!messageTime || !Number.isFinite(messageTime)) {
    return fallbackSentAt;
  }

  const sentDate = new Date(messageTime);
  if (Number.isNaN(sentDate.getTime())) {
    return fallbackSentAt;
  }

  const now = new Date();
  const diffMs = Math.max(now.getTime() - sentDate.getTime(), 0);
  const dayDiff = Math.round((getStartOfDayTime(now) - getStartOfDayTime(sentDate)) / DAY_MS);

  if (dayDiff > 1) {
    return `${formatDate(sentDate)} ${isEnglishLang() ? 'at' : 'в'} ${formatHours(sentDate)}`;
  }

  if (dayDiff === 1) {
    return `${isEnglishLang() ? 'Yesterday' : 'Вчера'} ${isEnglishLang() ? 'at' : 'в'} ${formatHours(sentDate)}`;
  }

  if (diffMs < MINUTE_MS) {
    return isEnglishLang() ? 'just now' : 'только что';
  }

  if (diffMs < HOUR_MS) {
    return formatMinutesAgo(Math.floor(diffMs / MINUTE_MS));
  }

  if (diffMs < 4 * HOUR_MS) {
    return formatHoursAgo(Math.floor(diffMs / HOUR_MS));
  }

  return `${isEnglishLang() ? 'at' : 'в'} ${formatHours(sentDate)}`;
}
